import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  useRef,
  startTransition,
} from 'react';
import Toast from '@/shared/components/ui/Toast.jsx';

const ToastContext = createContext(null);

const TOAST_ACTIONS = {
  ADD: 'ADD_TOAST',
  REMOVE: 'REMOVE_TOAST',
  CLEAR: 'CLEAR_TOASTS',
};

function toastReducer(state, action) {
  switch (action.type) {
    case TOAST_ACTIONS.ADD:
      return [...state, action.payload];
    case TOAST_ACTIONS.REMOVE:
      return state.filter((toast) => toast.id !== action.payload);
    case TOAST_ACTIONS.CLEAR:
      return [];
    default:
      return state;
  }
}

// eslint-disable-next-line react-refresh/only-export-components
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, dispatch] = useReducer(toastReducer, []);
  const timeoutsRef = useRef(new Map());

  const showToast = useCallback(
    ({ message, type = 'success', duration = 3000 }) => {
      const id = Date.now() + Math.random();

      startTransition(() => {
        dispatch({
          type: TOAST_ACTIONS.ADD,
          payload: {
            id,
            message,
            type,
            duration,
          },
        });
      });

      if (duration) {
        const timeoutId = setTimeout(() => {
          startTransition(() => {
            dispatch({
              type: TOAST_ACTIONS.REMOVE,
              payload: id,
            });
          });
          timeoutsRef.current.delete(id);
        }, duration);

        timeoutsRef.current.set(id, timeoutId);
      }
    },
    [],
  );

  const hideToast = useCallback((id) => {
    const timeoutId = timeoutsRef.current.get(id);
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutsRef.current.delete(id);
    }

    startTransition(() => {
      dispatch({
        type: TOAST_ACTIONS.REMOVE,
        payload: id,
      });
    });
  }, []);

  const clearAllToasts = useCallback(() => {
    timeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
    timeoutsRef.current.clear();

    startTransition(() => {
      dispatch({ type: TOAST_ACTIONS.CLEAR });
    });
  }, []);

  const value = useMemo(
    () => ({
      showToast,
      hideToast,
      clearAllToasts,
    }),
    [showToast, hideToast, clearAllToasts],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed bottom-4 left-4 z-50 flex flex-col gap-2">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => hideToast(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export default ToastProvider;
